/**
 * The Undo window — one lifecycle for every Undo toast in the app.
 *
 * Capture's Save, the Ledger's delete (bulk and from the Edit sheet), Settings'
 * category delete and Sounding's Save all offer the same affordance: a toast
 * that lives for five seconds and reverses what was just done (SPEC.md §3.3,
 * §7). Each view used to keep its own timer, and the copies drifted — Sounding's
 * never had one at all, so its toast sat on top of the dock until dismissed by
 * hand. This hook is the one place the lifecycle lives:
 *
 * - **It closes by itself** after {@link UNDO_WINDOW_MS}. A new action replaces
 *   the old one and restarts the window.
 * - **Undo commits before it closes.** The toast stays up until the reversal
 *   has actually landed. Dismissing first and firing the write into the void
 *   loses the undo if the user navigates in the same instant — and they will,
 *   because "undo, then leave" is the whole point of the gesture. While the
 *   reversal is in flight the window's own timer cannot close it, and a second
 *   tap is ignored.
 * - **A failed reversal is reported** through `onError`, never swallowed
 *   (INV-7), and the toast closes: offering an Undo that has just failed would
 *   be a control that lies about what it does (D-7).
 *
 * The reversal itself is the caller's: a tombstone or a restore through the
 * repo, so the affordance stays honest about INV-5. Nothing here touches the
 * network; Capture's write path is untouched by it (INV-4).
 */

import { useCallback, useEffect, useRef, useState } from "react";

/** How long an Undo toast lives (SPEC.md §3.3). */
export const UNDO_WINDOW_MS = 5_000;

export interface UndoWindow<T> {
  /** What the toast is offering to reverse, or null when it is closed. */
  pending: T | null;
  /** True while a reversal is in flight — the toast stays up until it commits. */
  undoing: boolean;
  /** Offer an Undo for `payload`, replacing any open one and restarting the window. */
  open: (payload: T) => void;
  /** Reverse the pending action. A no-op when closed or already reversing. */
  undo: () => void;
  /** Close without reversing anything. */
  dismiss: () => void;
}

export function useUndoWindow<T>(options: {
  /** Reverse the action. The toast closes once this settles. */
  onUndo: (payload: T) => Promise<void>;
  /** Told when the reversal fails, so the view can say so (INV-7). */
  onError: (cause: unknown) => void;
}): UndoWindow<T> {
  const [pending, setPending] = useState<T | null>(null);
  const [undoing, setUndoing] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The synchronous mirror of `undoing`, so a double tap inside one frame is
  // refused before React has re-rendered with the new state.
  const inFlight = useRef(false);
  // Bumped on every `open`, so a reversal that settles after a newer action has
  // opened its own toast closes nothing but its own.
  const generation = useRef(0);

  // The latest callbacks, so `undo` never reverses through a stale closure.
  const callbacks = useRef(options);
  useEffect(() => {
    callbacks.current = options;
  });

  const clearTimer = useCallback((): void => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
  }, []);

  const open = useCallback(
    (payload: T): void => {
      clearTimer();
      generation.current += 1;
      setPending(payload);
      timer.current = setTimeout(() => {
        timer.current = null;
        setPending(null);
      }, UNDO_WINDOW_MS);
    },
    [clearTimer],
  );

  const dismiss = useCallback((): void => {
    clearTimer();
    setPending(null);
  }, [clearTimer]);

  const undo = useCallback((): void => {
    if (pending === null || inFlight.current) return;
    clearTimer();
    inFlight.current = true;
    setUndoing(true);

    const reversing = generation.current;
    const settle = (): void => {
      inFlight.current = false;
      setUndoing(false);
      if (generation.current === reversing) setPending(null);
    };
    callbacks.current
      .onUndo(pending)
      .then(settle)
      .catch((cause: unknown) => {
        settle();
        callbacks.current.onError(cause);
      });
  }, [pending, clearTimer]);

  useEffect(() => clearTimer, [clearTimer]);

  return { pending, undoing, open, undo, dismiss };
}
